// pages/admin/AdminResearchPage.tsx — the T1 RESEARCH DEPT human-review surface
// (HUMAN-UI-MAP stage 1). Three tabs = the three human "2%" gates:
//   Niche Finder — ranked niches → PICK.
//   Creators     — scored creators → keep / drop.
//   Candidates   — the kept videos → approve (→ teardown) / reject.
// The AI does the 98% (harvest + score); the human confirms here, and the
// corrections train the self-improving loop.
import { CampaignsTab } from "./research/CampaignsTab.tsx";
import { useState, useEffect, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import { KalodataUpload } from "./KalodataUpload.tsx";
import { teardownsApi } from "../../api/covers-teardowns.ts";
import {
  researchApi,
  type NicheRank, type ResearchCreator, type ResearchCandidate,
  type ManagedNiche, type ManagedKeyword, type Circle, type GridCell,
} from "../../api/research.ts";

type Tab = "niches" | "creators" | "candidates" | "manage" | "arcs" | "grid" | "campaigns";

const num = (n: number | null | undefined) => (n == null ? "—" : Intl.NumberFormat().format(n));
const pct = (n: number | null | undefined) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);
const score = (n: number | null | undefined) => (n == null ? "—" : n.toFixed(2));

const card: React.CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff" };
const btn: React.CSSProperties = { padding: "6px 12px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13 };
const btnPrimary: React.CSSProperties = { ...btn, background: "#2f855a", color: "#fff", border: 0 };
const btnDanger: React.CSSProperties = { ...btn, background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" };

export function AdminResearchPage() {
  const [tab, setTab] = useState<Tab>("manage");
  const [showImport, setShowImport] = useState(false);
  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Research</h1>
        <button onClick={() => setShowImport(true)}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Import Kalodata creators
        </button>
      </div>
      {showImport && <KalodataUpload onClose={() => setShowImport(false)} />}
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        The sourcing funnel's human gates. AI harvests + scores; you confirm the 2% that trains the loop.
        <br />
        <span style={{ fontSize: 12 }}>
          Workflow: <strong>1.</strong> Manage Niches — create a niche (generates keywords) →
          <strong> 2.</strong> harvest with the Chrome extension →
          <strong> 3.</strong> review Creators &amp; Candidates →
          <strong> 4.</strong> the Niche Finder ranks what's working.
        </span>
      </p>
      <div style={{ display: "flex", gap: 6, margin: "14px 0" }}>
        {(["campaigns", "manage", "candidates", "creators", "niches", "arcs", "grid"] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ ...btn, background: tab === t ? "#111827" : "#fff", color: tab === t ? "#fff" : "#111827", textTransform: "capitalize" }}>
            {t === "campaigns" ? "Campaigns" : t === "niches" ? "Niche Finder" : t === "manage" ? "Manage Niches" : t === "candidates" ? "Review Candidates" : t === "arcs" ? "Arcs" : t === "grid" ? "Grid" : "Review Creators"}
          </button>
        ))}
      </div>
      {tab === "niches" && <NicheFinderTab />}
      {tab === "creators" && <CreatorsTab />}
      {tab === "candidates" && <CandidatesTab />}
      {tab === "manage" && <ManageTab />}
      {tab === "arcs" && <ArcsTab />}
      {tab === "grid" && <GridTab />}
      {tab === "campaigns" && <CampaignsTab />}
    </div>
  );
}

// ── 1a NICHE FINDER ──────────────────────────────────────────────────────────────
function NicheFinderTab() {
  const [rows, setRows] = useState<NicheRank[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    setErr(null);
    researchApi.niches().then(setRows).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, []);
  useEffect(load, [load]);

  if (err) return <ErrorBox msg={err} onRetry={load} />;
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Empty msg="No harvest data yet. Create a niche in Manage Niches, then harvest with the extension — rankings appear here once candidates arrive." />;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: "left", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
          <Th>Niche</Th><Th>Score</Th><Th>Kept</Th><Th>Candidates</Th><Th>Avg video score</Th><Th>Affiliate</Th><Th>Last harvest</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((n) => (
          <tr key={n.nicheId} style={{ borderBottom: "1px solid #f3f4f6" }}>
            <Td><strong>{n.vertical}</strong>{n.subVertical ? ` › ${n.subVertical}` : ""}{n.parentNicheId ? "" : <span style={{ color: "#9ca3af" }}> (top)</span>}</Td>
            <Td><span style={{ fontWeight: 700, color: "#2f855a" }}>{score(n.nicheScore)}</span></Td>
            <Td>{num(n.kept)}</Td>
            <Td>{num(n.candidates)}</Td>
            <Td>{score(n.avgScore)}</Td>
            <Td>{pct(n.affiliateShare)}</Td>
            <Td style={{ color: "#9ca3af" }}>{n.lastHarvest ? new Date(n.lastHarvest).toLocaleDateString() : "—"}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── 1b CREATORS ──────────────────────────────────────────────────────────────────
function CreatorsTab() {
  const [rows, setRows] = useState<ResearchCreator[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [niche, setNiche] = useState("");
  const [intent, setIntent] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setErr(null); setRows(null);
    researchApi.creators({ niche: niche || undefined, intent: intent || undefined, status: "active" })
      .then(setRows).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, [niche, intent]);
  useEffect(load, [load]);

  const act = async (id: string, action: "keep" | "drop") => {
    setBusy(id);
    try {
      if (action === "keep") await researchApi.keepCreator(id);
      else await researchApi.dropCreator(id);
      setRows((r) => (r ?? []).filter((c) => c.id !== id || action === "keep"));
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <Filters niche={niche} setNiche={setNiche} intent={intent} setIntent={setIntent} />
      {err ? <ErrorBox msg={err} onRetry={load} /> : !rows ? <Loading /> : rows.length === 0 ? (
        <Empty msg="No saved creators yet. Use the extension's Save-creator button on a niche's growing creators." />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
          {rows.map((c) => (
            <div key={c.id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <strong>@{c.handle}</strong>
                <span style={{ fontWeight: 700, color: "#2f855a" }}>{score(c.creatorScore)}</span>
              </div>
              <div style={{ color: "#6b7280", fontSize: 12, margin: "4px 0 10px" }}>
                {c.niche} · {c.intent === "audience_growth" ? "growth" : "sales"} · {c.platform}
              </div>
              <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.8 }}>
                <div>growth-rate: <strong>{pct(c.followerGrowthRate)}</strong> <span style={{ color: "#9ca3af" }}>(followers {num(c.followerCount)})</span></div>
                <div>share {pct(c.avgShareRate)} · save {pct(c.avgSaveRate)} · consistency {score(c.consistencyScore)}</div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button style={btnPrimary} disabled={busy === c.id} onClick={() => act(c.id, "keep")}>Keep</button>
                <button style={btnDanger} disabled={busy === c.id} onClick={() => act(c.id, "drop")}>Drop</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 1c CANDIDATES ────────────────────────────────────────────────────────────────
function CandidatesTab() {
  const [rows, setRows] = useState<ResearchCandidate[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [niche, setNiche] = useState("");
  const [intent, setIntent] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setErr(null); setRows(null);
    researchApi.candidates({ niche: niche || undefined, intent: intent || undefined, status: "kept" })
      .then(setRows).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, [niche, intent]);
  useEffect(load, [load]);

  const act = async (id: string, action: "approve" | "reject") => {
    setBusy(id);
    try {
      if (action === "approve") await researchApi.approveCandidate(id);
      else await researchApi.rejectCandidate(id);
      setRows((r) => (r ?? []).filter((c) => c.id !== id));
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <Filters niche={niche} setNiche={setNiche} intent={intent} setIntent={setIntent} />
      {err ? <ErrorBox msg={err} onRetry={load} /> : !rows ? <Loading /> : rows.length === 0 ? (
        <Empty msg="No candidates awaiting review. Harvest some with the extension, then they appear here once scored + kept." />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {rows.map((c) => (
            <div key={c.id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <a href={c.sourceUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontSize: 13, wordBreak: "break-all" }}>
                  {c.handle ? `@${c.handle}` : "view source"}
                </a>
                <span style={{ fontWeight: 700, color: "#2f855a" }}>{score(c.videoScore)}</span>
              </div>
              <div style={{ color: "#6b7280", fontSize: 12, margin: "4px 0 10px" }}>
                {c.niche} · {c.intent === "audience_growth" ? "growth" : "sales"}{c.mp4Url ? " · mp4 ✓" : ""}
              </div>
              <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.8 }}>
                <div>views {num(c.views)} · shares {num(c.shares)} · saves {num(c.saves)}</div>
                <div>share {pct(c.shareRate)} · save {pct(c.saveRate)}</div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button style={btnPrimary} disabled={busy === c.id} onClick={() => act(c.id, "approve")}>→ Teardown</button>
                <button style={btnDanger} disabled={busy === c.id} onClick={() => act(c.id, "reject")}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MANAGE (create/archive niches · list/add/drop keywords) ─────────────────────
function ManageTab() {
  const [niches, setNiches] = useState<ManagedNiche[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(() => {
    setErr(null);
    researchApi.manageNiches().then(setNiches).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, []);
  useEffect(load, [load]);

  return (
    <div>
      <CreateNicheForm onCreated={load} />
      {err ? <ErrorBox msg={err} onRetry={load} /> : !niches ? <Loading /> : niches.length === 0 ? (
        <Empty msg="No niches yet. Create one above — it generates the framework keywords automatically." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {niches.map((n) => (
            <div key={n.id} style={{ ...card, opacity: n.status === "archived" ? 0.55 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong>{n.vertical}</strong>{n.subVertical ? ` › ${n.subVertical}` : ""}
                  {n.parentNicheId ? <span style={{ color: "#9ca3af", fontSize: 12 }}> (sub-niche)</span> : ""}
                  {n.status === "archived" && <span style={{ color: "#b91c1c", fontSize: 12 }}> · archived</span>}
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
                    {n.keywordCount} keywords · {n.candidateCount} candidates
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={btn} onClick={() => setExpanded(expanded === n.id ? null : n.id)}>
                    {expanded === n.id ? "Hide keywords" : "Keywords"}
                  </button>
                  {n.status === "active"
                    ? <button style={btnDanger} onClick={() => researchApi.archiveNiche(n.id).then(load)}>Archive</button>
                    : <button style={btn} onClick={() => researchApi.activateNiche(n.id).then(load)}>Activate</button>}
                </div>
              </div>
              {expanded === n.id && <KeywordManager nicheId={n.id} onChange={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateNicheForm({ onCreated }: { onCreated: () => void }) {
  const [vertical, setVertical] = useState("");
  const [subVertical, setSubVertical] = useState("");
  const [parentVertical, setParentVertical] = useState("");
  const [products, setProducts] = useState("");
  const [problems, setProblems] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async () => {
    if (!vertical.trim()) { setMsg("Vertical is required"); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await researchApi.createNiche({
        vertical: vertical.trim(),
        subVertical: subVertical.trim() || null,
        parentVertical: parentVertical.trim() || null,
        products: products.split(",").map((s) => s.trim()).filter(Boolean),
        problems: problems.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setMsg(`${r.created ? "Created" : "Updated"} — ${r.keywordsAdded} keywords generated`);
      setVertical(""); setSubVertical(""); setParentVertical(""); setProducts(""); setProblems("");
      onCreated();
    } catch (e) { setMsg("Failed: " + (e as Error).message); }
    finally { setBusy(false); }
  };

  const inp: React.CSSProperties = { padding: 7, borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13 };
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>Create a niche</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <input style={inp} placeholder="vertical (e.g. fitness) *" value={vertical} onChange={(e) => setVertical(e.target.value)} />
        <input style={inp} placeholder="sub-vertical (optional, e.g. home workouts)" value={subVertical} onChange={(e) => setSubVertical(e.target.value)} />
        <input style={inp} placeholder="parent vertical (optional — makes this a sub-niche)" value={parentVertical} onChange={(e) => setParentVertical(e.target.value)} />
        <div />
        <input style={inp} placeholder="products, comma-separated (seeds sales keywords)" value={products} onChange={(e) => setProducts(e.target.value)} />
        <input style={inp} placeholder="problems, comma-separated (seeds growth keywords)" value={problems} onChange={(e) => setProblems(e.target.value)} />
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button style={btnPrimary} disabled={busy} onClick={submit}>{busy ? "Creating…" : "Create + generate keywords"}</button>
        {msg && <span style={{ fontSize: 12, color: msg.startsWith("Failed") ? "#b91c1c" : "#2f855a" }}>{msg}</span>}
      </div>
    </div>
  );
}

function KeywordManager({ nicheId, onChange }: { nicheId: string; onChange: () => void }) {
  const [kws, setKws] = useState<ManagedKeyword[] | null>(null);
  const [kwErr, setKwErr] = useState<string | null>(null);
  const [newKw, setNewKw] = useState("");
  const [newIntent, setNewIntent] = useState<"audience_growth" | "sales" | "">("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    // (tf3-31, A17) a failed read is shown, never "no keywords"
    setKwErr(null);
    researchApi.keywords(nicheId).then(setKws).catch((e) => setKwErr(String((e as Error)?.message ?? e)));   // catch-reason: the error is shown above the list
  }, [nicheId]);
  useEffect(load, [load]);

  const add = async () => {
    if (!newKw.trim()) return;
    setBusy(true);
    try {
      await researchApi.addKeyword(nicheId, newKw.trim(), newIntent || null);
      setNewKw(""); setNewIntent(""); load(); onChange();
    } finally { setBusy(false); }
  };
  const drop = async (id: string) => { await researchApi.dropKeyword(id); load(); onChange(); };
  const reactivate = async (id: string) => { await researchApi.activateKeyword(id); load(); onChange(); };

  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #f3f4f6", paddingTop: 12 }}>
      {kwErr && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 8 }}>The keywords couldn't be read: {kwErr}</div>}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input style={{ flex: 1, padding: 6, borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}
          placeholder="add a keyword…" value={newKw} onChange={(e) => setNewKw(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
        <select style={{ padding: 6, borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}
          value={newIntent} onChange={(e) => setNewIntent(e.target.value as "audience_growth" | "sales" | "")}>
          <option value="">intent…</option>
          <option value="audience_growth">growth</option>
          <option value="sales">sales</option>
        </select>
        <button style={btnPrimary} disabled={busy} onClick={add}>Add</button>
      </div>
      {!kws ? (kwErr ? null : <span style={{ color: "#9ca3af", fontSize: 12 }}>loading…</span>) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {kws.map((k) => (
            <span key={k.id} title={`${k.pattern ?? ""}${k.keywordScore != null ? ` · score ${k.keywordScore.toFixed(2)}` : ""}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "4px 8px",
                borderRadius: 6, background: k.status === "dropped" ? "#f3f4f6" : "#eef2ff",
                color: k.status === "dropped" ? "#9ca3af" : "#3730a3",
                textDecoration: k.status === "dropped" ? "line-through" : "none" }}>
              {k.keyword}
              {k.intent && <em style={{ color: "#6b7280", fontStyle: "normal" }}>· {k.intent === "audience_growth" ? "g" : "s"}</em>}
              {k.status === "dropped"
                ? <button aria-label="Reactivate keyword" onClick={() => reactivate(k.id)} style={{ border: 0, background: "none", cursor: "pointer", color: "#2563eb", fontSize: 12 }}>↺</button>
                : <button aria-label="Drop keyword" onClick={() => drop(k.id)} style={{ border: 0, background: "none", cursor: "pointer", color: "#b91c1c", fontSize: 13 }}>×</button>}
            </span>
          ))}
          {kws.length === 0 && <span style={{ color: "#9ca3af", fontSize: 12 }}>no keywords</span>}
        </div>
      )}
    </div>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────────
function Filters({ niche, setNiche, intent, setIntent }: { niche: string; setNiche: (s: string) => void; intent: string; setIntent: (s: string) => void }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
      <input placeholder="filter by niche" value={niche} onChange={(e) => setNiche(e.target.value)}
        style={{ padding: 7, borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13 }} />
      <select value={intent} onChange={(e) => setIntent(e.target.value)}
        style={{ padding: 7, borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13 }}>
        <option value="">all intents</option>
        <option value="audience_growth">growth</option>
        <option value="sales">sales</option>
      </select>
    </div>
  );
}
const Th = ({ children }: { children: React.ReactNode }) => <th style={{ padding: "8px 10px", fontWeight: 600 }}>{children}</th>;
const Td = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => <td style={{ padding: "8px 10px", ...style }}>{children}</td>;
const Loading = () => <div style={{ color: "#9ca3af", padding: 20 }}>Loading…</div>;
const Empty = ({ msg }: { msg: string }) => <div style={{ color: "#9ca3af", padding: 20 }}>{msg}</div>;
const ErrorBox = ({ msg, onRetry }: { msg: string; onRetry: () => void }) => (
  <div style={{ color: "#b91c1c", padding: 20 }}>
    {msg} <button style={{ ...btn, marginLeft: 8 }} onClick={onRetry}>retry</button>
  </div>
);

// ── ARCS (P2) — the arc leaderboard → select circles/videos → send to teardown ──────
function ArcsTab() {
  const [rows, setRows] = useState<Circle[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [channel, setChannel] = useState<"tiktok_shop" | "local_business" | "">("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set()); // candidateIds
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [sentResearchArcId, setSentResearchArcId] = useState<string | null>(null); // UX-1: researchArcId for a real link
  const [tearing, setTearing] = useState<string | null>(null); // sourceUrl being sent to teardown
  const teardownVideo = async (sourceUrl: string) => {
    setTearing(sourceUrl); setNote(null);
    try {
      await teardownsApi.submit([sourceUrl]);
      setNote(`Sent to teardown: ${sourceUrl.replace("https://www.tiktok.com", "")}. Track it on the Teardowns page.`);
    } catch (e) {
      setNote(`Teardown failed: ${(e as Error).message}`);
    } finally { setTearing(null); }
  };

  // (TF3-1, 10-14) "Make video" (a backend remix of the torn-down video, outside the engine) is DELETED (T9-3).

  const load = useCallback(() => {
    setErr(null); setRows(null); setSelected(new Set());
    researchApi.circles({ channel: channel || undefined })
      .then(setRows).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, [channel]);
  useEffect(load, [load]);

  // a circle's selectable candidate ids (harvested hits/ramps only; null candidateId can't teardown)
  const selectableIds = (c: Circle) => c.videos.map((v) => v.candidateId).filter((x): x is string => !!x);

  const toggleCircle = (c: Circle) => {
    const ids = selectableIds(c);
    const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOn) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleVideo = (candidateId: string) => {
    setSelected((prev) => { const next = new Set(prev); next.has(candidateId) ? next.delete(candidateId) : next.add(candidateId); return next; });
  };
  const toggleExpand = (key: string) => {
    setExpanded((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  };

  const send = async () => {
    if (!selected.size) return;
    setSending(true); setNote(null);
    try {
      const { queued, researchArcId } = await researchApi.sendArcsToTeardown([...selected]);
      // D1/UX-1: clean note + a REAL link (rendered below). Was a non-clickable string
      // pointing at /admin/research?arc (wrong page — the arc view is /admin/research-arcs).
      setNote(`Sent ${queued} video${queued === 1 ? "" : "s"}. They'll appear in the arc view as the funnel tears them down (a few minutes).`);
      setSentResearchArcId(researchArcId);
      setSelected(new Set());
      load();
    } catch (e) { setNote("Failed: " + String((e as Error).message ?? e)); setSentResearchArcId(null); }
    finally { setSending(false); }
  };

  if (err) return <ErrorBox msg={err} onRetry={load} />;
  if (!rows) return <Loading />;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: "#6b7280" }}>Vertical:</span>
        {(["", "tiktok_shop", "local_business"] as const).map((v) => (
          <button key={v || "all"} onClick={() => setChannel(v)}
            style={{ ...btn, background: channel === v ? "#111827" : "#fff", color: channel === v ? "#fff" : "#111827" }}>
            {v === "" ? "All" : v}
          </button>
        ))}
        <button onClick={load} style={btn}>Refresh</button>
      </div>

      {rows.length === 0
        ? <Empty msg="No arcs yet. The leaderboard crons build these from swept creators' catalogs — they appear once hits are detected." />
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                <Th> </Th><Th>Sub-niche</Th><Th>Persona</Th><Th>Arc</Th><Th>Members</Th><Th>Performance</Th><Th>Velocity</Th><Th>Rank</Th><Th>Videos</Th><Th> </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const ids = selectableIds(c);
                const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
                const someOn = ids.some((id) => selected.has(id));
                const isOpen = expanded.has(c.circleKey);
                return (
                  <Fragment key={c.circleKey}>
                    <tr style={{ borderBottom: "1px solid #f3f4f6", background: someOn ? "#f0fdf4" : undefined }}>
                      <td style={{ padding: "6px 10px" }}>
                        <input type="checkbox" checked={allOn} ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                          disabled={ids.length === 0} onChange={() => toggleCircle(c)} />
                      </td>
                      <td style={{ padding: "6px 10px" }}>{c.subNiche}</td>
                      <td style={{ padding: "6px 10px", color: c.personaLabel ? "#111827" : "#9ca3af" }}>{c.personaLabel ?? "— unlabeled —"}</td>
                      <td style={{ padding: "6px 10px", color: c.arcLabel ? "#111827" : "#9ca3af" }}>{c.arcLabel ?? "— unlabeled —"}</td>
                      <td style={{ padding: "6px 10px" }}>{c.memberCount}</td>
                      <td style={{ padding: "6px 10px" }}>{c.performance == null ? "—" : c.performance.toFixed(2)}</td>
                      <td style={{ padding: "6px 10px" }}>{c.velocity == null ? "—" : c.velocity.toFixed(2)}</td>
                      <td style={{ padding: "6px 10px" }}>{c.rankScore == null ? "—" : c.rankScore.toFixed(3)}</td>
                      <td style={{ padding: "6px 10px" }}>{c.videos.length}{ids.length < c.videos.length ? <span style={{ color: "#9ca3af" }}> ({c.videos.length - ids.length} unharvested)</span> : null}</td>
                      <td style={{ padding: "6px 10px" }}>
                        <button onClick={() => toggleExpand(c.circleKey)} style={btn}>{isOpen ? "▾ hide" : "▸ videos"}</button>
                      </td>
                    </tr>
                    {isOpen && c.videos.map((v) => (
                      <tr key={c.circleKey + v.sourceUrl} style={{ background: "#fafafa", fontSize: 12 }}>
                        <td style={{ padding: "4px 10px" }}>
                          <input type="checkbox" disabled={!v.candidateId}
                            checked={!!v.candidateId && selected.has(v.candidateId)}
                            onChange={() => v.candidateId && toggleVideo(v.candidateId)} />
                        </td>
                        <td colSpan={3} style={{ padding: "4px 10px", color: v.candidateId ? "#374151" : "#9ca3af" }}>
                          <a href={v.sourceUrl} target="_blank" rel="noreferrer" style={{ color: "inherit" }}>{v.sourceUrl.replace("https://www.tiktok.com", "")}</a>
                          {!v.candidateId ? " (not harvested — can't teardown)" : ""}
                        </td>
                        <td style={{ padding: "4px 10px" }}>@{v.creatorHandle}</td>
                        <td style={{ padding: "4px 10px" }}>{v.role}</td>
                        <td colSpan={2} style={{ padding: "4px 10px" }}>
                          {v.spikeRatio == null ? "" : `${v.spikeRatio.toFixed(1)}× spike`}
                          {v.teardownJobId
                            ? <span style={{ marginLeft: 10, fontSize: 12, color: "#0891b2" }} title="a teardown job exists (it may still be running)">teardown ordered</span>
                            : v.candidateId
                              ? <button onClick={() => teardownVideo(v.sourceUrl)} disabled={tearing === v.sourceUrl}
                                  style={{ ...btn, marginLeft: 10, padding: "3px 10px", fontSize: 12 }}
                                  title="Send this winning video to teardown">
                                  {tearing === v.sourceUrl ? "…" : "✂ Teardown"}
                                </button>
                              : null}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}

      {/* sticky action bar */}
      <div style={{ position: "sticky", bottom: 0, marginTop: 14, padding: "10px 0", background: "#fff", borderTop: "1px solid #e5e7eb", display: "flex", gap: 12, alignItems: "center" }}>
        <button onClick={send} disabled={!selected.size || sending} style={{ ...btnPrimary, opacity: !selected.size || sending ? 0.5 : 1 }}>
          {sending ? "Sending…" : `Send ${selected.size} video${selected.size === 1 ? "" : "s"} to teardown`}
        </button>
        {selected.size > 0 && <button onClick={() => setSelected(new Set())} style={btn}>Clear</button>}
        {note && <span style={{ fontSize: 13, color: note.startsWith("Failed") ? "#b91c1c" : "#2f855a" }}>{note}</span>}
        {sentResearchArcId && (
          <Link to={`/admin/research-arcs?arc=${sentResearchArcId}`} style={{ fontSize: 13, color: "#2563eb", fontWeight: 600 }}>
            Open arc view →
          </Link>
        )}
      </div>
    </div>
  );
}


function GridTab() {
  const [rows, setRows] = useState<GridCell[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tier, setTier] = useState<"" | "proven_sales" | "confirmed_organic">("");

  useEffect(() => {
    setErr(null);
    researchApi.grid({ tier: tier || undefined })
      .then(setRows)
      .catch((e) => setErr(String((e as Error).message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, [tier]);

  // parse creatorSig "[physique,gender,age,persona]" → the persona slot (index 3)
  const persona = (sig: string) => { const p = (sig || "").split(",")[3]; return p && p !== "*" ? p : "—"; };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <strong>Creator × Offering grid</strong>
        {(["", "proven_sales", "confirmed_organic"] as const).map((t) => (
          <button key={t || "all"} onClick={() => setTier(t)}
            style={{ ...btn, background: tier === t ? "#111827" : "#fff", color: tier === t ? "#fff" : "#111827" }}>
            {t === "" ? "All tiers" : t === "proven_sales" ? "Proven" : "Organic"}
          </button>
        ))}
      </div>
      {err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}
      {!rows ? <div style={{ color: "#6b7280" }}>Loading…</div>
        : rows.length === 0 ? <div style={{ color: "#6b7280" }}>No grid cells yet — cells form once ≥2 creators share a (persona × offering) intersection.</div>
        : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>
            <Th>Persona</Th><Th>Offering</Th><Th>Leaf</Th><Th>Creators</Th><Th>Top rank</Th><Th>Avg conv.</Th><Th>Tier</Th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.cellKey} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "6px 10px", color: persona(c.creatorSig) === "—" ? "#9ca3af" : "#111827" }}>{persona(c.creatorSig)}</td>
                <td style={{ padding: "6px 10px" }}>{c.offeringSig}</td>
                <td style={{ padding: "6px 10px", color: "#6b7280" }}>{c.categoryLeaf ?? "—"}</td>
                <td style={{ padding: "6px 10px" }}>{c.creatorCount}</td>
                <td style={{ padding: "6px 10px" }}>{c.topRankScore != null ? c.topRankScore.toFixed(3) : "—"}</td>
                <td style={{ padding: "6px 10px" }}>{c.avgConversion != null ? c.avgConversion.toFixed(2) : "—"}</td>
                <td style={{ padding: "6px 10px" }}>
                  <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11,
                    background: c.tier === "proven_sales" ? "#dcfce7" : c.tier === "confirmed_organic" ? "#e0e7ff" : "#f3f4f6",
                    color: c.tier === "proven_sales" ? "#166534" : c.tier === "confirmed_organic" ? "#3730a3" : "#6b7280" }}>
                    {c.tier === "proven_sales" ? "proven" : c.tier === "confirmed_organic" ? "organic" : "provisional"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
